import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { invoicesApi } from "@web-src/modules/api/createApi";
import { v4 as uuid4 } from "uuid";
import {
  useDebouncedCallback,
  useTranslations,
  useBlockNavigation,
} from "@jugl-web/utils";
import { InvoicesConfigModel, InvoiceSettingsParams } from "@jugl-web/rest-api";
import { InvoiceTaxRow } from "@web-src/modules/invoices/types";
import { InvoicesSettingsPageContext } from "./InvoicesSettingsPageContext";
import { useInvoicesConfigList } from "../pages/InvoicesBusinessInformationPage/hooks/useInvoicesConfigList";
import { useTaxFields } from "../hooks/useTaxFields";
import { TaxFields } from "../../InvoicesItemFormPage/context/InvoicesItemFormPageContext";
import { InvoicesSettingsDiscardAlert } from "../components/InvoicesSettingsDiscardAlert";

interface ComponentProps {
  children: React.ReactNode;
  initialData?: InvoiceSettingsParams & {
    dueIn: number;
    customFields: { name: string; value: string }[];
    taxFields: TaxFields;
    logo: File | undefined;
  };
}

export const InvoicesSettingsPageProvider = ({
  children,
  initialData,
}: ComponentProps) => {
  const confirmNavigation = useRef<(() => void) | null>(null);
  const skipNavigationAlert = useRef(false);
  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    useState(false);
  useBlockNavigation(
    setIsDiscardChangesDialogOpen,
    confirmNavigation,
    skipNavigationAlert
  );

  const { t } = useTranslations();
  const { entity } = useEntitySelectedProvider();

  const [createConfig, { isLoading: isCreating }] =
    invoicesApi.useCreateConfigMutation();
  const [updateConfig, { isLoading: isUpdating }] =
    invoicesApi.useUpdateConfigMutation();
  const [deleteConfigLogo] = invoicesApi.useDeleteConfigLogoMutation();

  const { configId, refetch: refetchConfigId } = useInvoicesConfigList();

  const getReponseData = invoicesApi.useGetConfigQuery(
    {
      entityId: entity.id,
      configId,
    },
    {
      skip: !configId || !entity.id,
    }
  );

  const configData = getReponseData.data?.data;

  const [customFields, setCustomFields] = useState<
    { name: string; value: string }[]
  >(initialData?.customFields || []);

  const {
    taxFields,
    handleAddTaxField,
    handleEditTaxField,
    handleRemoveTaxField,
    handleSetDefaultTaxField,
  } = useTaxFields(initialData?.taxFields);

  const [logo, setLogo] = useState<File | undefined>(initialData?.logo);

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    control,
  } = useForm<InvoiceSettingsParams>({
    mode: "onChange",
    defaultValues: {
      name: initialData?.name || "",
      street: initialData?.street || "",
      state: initialData?.state || "",
      country: initialData?.country || "",
      city: initialData?.city || "",
      zipCode: initialData?.zipCode || "",
      dueIn: initialData?.dueIn || 7,
      // TODO temporary hidden 22.06.2025, for future releases
      // prefix: "",
      note: initialData?.note || "",
    },
  });

  const formValues = watch();

  const withSkipAlert = (fn: () => void) => {
    fn();
    skipNavigationAlert.current = false;
  };

  const submitForm = useCallback(
    async (
      overrideValues?: Partial<{
        customFields: { name: string; value: string }[];
        logo: File | null;
      }>
    ) => {
      handleSubmit(async (values) => {
        try {
          const payload: Omit<InvoicesConfigModel, "id" | "logo"> = {
            name: uuid4(),
            header: {
              company_name: values.name,
              street: values.street || "",
              state: values.state || "",
              city: values.city || "",
              country: values.country || "",
              zip_code: values.zipCode || "",
              custom_field: (
                overrideValues?.customFields || customFields
              ).reduce(
                (acc, field) => {
                  acc[field.name] = field.value;
                  return acc;
                },
                {} as Record<string, string>
              ),
            },
            footer: {
              notes: values.note || "",
            },
            tax: [
              ...taxFields.invoice.map((field) => ({
                name: field.name,
                type: "total",
                percentage: field.percentage,
                default: field.default,
              })),
              ...taxFields.item.map((field) => ({
                name: field.name,
                type: "item",
                percentage: field.percentage,
                default: field.default,
              })),
            ],
            due_in: values.dueIn,
            custom_field: configData?.custom_field || [],
          };

          if (configData) {
            skipNavigationAlert.current = true;
          }

          if (configId) {
            const response = await updateConfig({
              entityId: entity.id,
              configId,
              payload,
              logo: overrideValues?.logo ?? logo ?? null,
            });

            if ("data" in response && response?.data) {
              skipNavigationAlert.current = true;
            }
          } else {
            const response = await createConfig({
              entityId: entity.id,
              payload,
              logo,
              meta: {
                successMessage: t({
                  id: "invoices.settings.create-success",
                  defaultMessage: "Config created successfully",
                }),
                getErrorMessage: () =>
                  t({
                    id: "invoices.settings.create-error",
                    defaultMessage: "Error updating config",
                  }),
              },
            });

            if ("data" in response) {
              refetchConfigId();
              skipNavigationAlert.current = true;
            }
          }
        } finally {
          getReponseData.refetch();
        }
      })();
    },
    [
      handleSubmit,
      customFields,
      taxFields.invoice,
      taxFields.item,
      configData,
      configId,
      updateConfig,
      entity.id,
      logo,
      createConfig,
      t,
      refetchConfigId,
      getReponseData,
    ]
  );

  const debouncedSubmitFn = useDebouncedCallback(submitForm, 500);

  watch(() => {
    if (configId) {
      debouncedSubmitFn();
    }
  });

  const value = useMemo(
    () => ({
      formValues,
      register,
      errors,
      watch,
      customFields,
      setCustomFields: (newCustomFields: { name: string; value: string }[]) =>
        withSkipAlert(() => {
          setCustomFields(newCustomFields);
          if (configId) {
            debouncedSubmitFn({
              customFields: [...customFields, ...newCustomFields],
            });
          }
        }),
      logo,
      setLogo: (newLogo: File | undefined) =>
        withSkipAlert(() => {
          setLogo(newLogo);

          if (configId && newLogo === undefined) {
            deleteConfigLogo({
              entityId: entity.id,
              configId,
            });
          } else {
            debouncedSubmitFn({ logo: newLogo ?? null });
          }
        }),
      taxFields,
      addTaxField: (type: "item" | "invoice", field: InvoiceTaxRow) =>
        withSkipAlert(() => handleAddTaxField(type, field)),
      editTaxField: (type: "item" | "invoice", field: InvoiceTaxRow) =>
        withSkipAlert(() => handleEditTaxField(type, field)),
      removeTaxField: (type: "item" | "invoice", fieldId: string) =>
        withSkipAlert(() => handleRemoveTaxField(type, fieldId)),
      setTaxFieldAsDefault: (
        type: "item" | "invoice",
        fieldId: string,
        isDefault: boolean
      ) =>
        withSkipAlert(() => handleSetDefaultTaxField(type, fieldId, isDefault)),
      submitForm: debouncedSubmitFn,
      isSaving: isCreating || isUpdating,
      configId,
      control,
    }),
    [
      formValues,
      register,
      errors,
      watch,
      customFields,
      logo,
      taxFields,
      handleAddTaxField,
      handleEditTaxField,
      handleRemoveTaxField,
      handleSetDefaultTaxField,
      debouncedSubmitFn,
      isCreating,
      isUpdating,
      configId,
      deleteConfigLogo,
      entity.id,
      control,
    ]
  );

  const isInitialized = useRef(false);
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    if (configId) {
      debouncedSubmitFn();
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [taxFields, configId]);

  useEffect(() => {
    if (configId) skipNavigationAlert.current = true;
  }, [configId]);

  return (
    <InvoicesSettingsPageContext.Provider value={value}>
      {children}
      <InvoicesSettingsDiscardAlert
        isOpen={isDiscardChangesDialogOpen}
        onCancel={() => {
          confirmNavigation.current = null;
          setIsDiscardChangesDialogOpen(false);
        }}
        onConfirm={() => {
          setIsDiscardChangesDialogOpen(false);
          confirmNavigation.current?.();
        }}
        onRequestClose={() => {
          setIsDiscardChangesDialogOpen(false);
        }}
      />
    </InvoicesSettingsPageContext.Provider>
  );
};
